import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";

import { useGetProgressPointsByPidQuery } from "api/memberPortal/project/progressPoints/progressPoints.api";
import { ExpandedEcrAssignmentRow } from "api/memberPortal/reports/expandedEcrAssignment/expandedEcrAssignment.types";

import ProgressPointRow from "../ProgressPointRow";

const ProgressPointsCell = ({
  row: { product_id, evaluator_checkout_date, validator_checkout_date },
}: {
  row: ExpandedEcrAssignmentRow;
}) => {
  const {
    data: progress_points,
    isLoading,
    isError,
  } = useGetProgressPointsByPidQuery(product_id || skipToken);

  const preparedProgressPointRows = useMemo(() => {
    if (!progress_points) return [];

    const filteredProgressPoints = progress_points
      .filter(
        (point) => point.progress_point === 6 || point.progress_point === 7,
      )
      .sort((a, b) => a.progress_point - b.progress_point);

    return filteredProgressPoints
      .map((point) => {
        // we need to add an extra row for pp6 or pp7 for estimated date if its in a certain status
        const needsExtraEstDate =
          (point.progress_point === 6 &&
            point.est_completion_dates.length > 0 &&
            ["Amended Package Needed", "Rejected"].includes(point.status)) ||
          (point.progress_point === 7 &&
            ["In Progress"].includes(point.status));

        // Determine the maximum number of entries based on the number of dates
        const maxEntries = Math.max(
          point.est_completion_dates.length,
          point.submission_dates.length,
          1,
        );

        // Create the base entries based on the maximum number of dates
        const entries = Array.from({ length: maxEntries }, (_, index) => {
          const suffix = index === 0 ? "" : String.fromCharCode(96 + index);

          let estDate = point.est_completion_dates[index]?.estimated_date;
          if (!estDate && index === 0) {
            estDate =
              point.progress_point === 6
                ? evaluator_checkout_date
                : validator_checkout_date;
          }

          const subDate = point.submission_dates[index]?.estimated_date;

          return {
            key: `${point.product.product_id}-${point.progress_point}${suffix}-${point.prog_id}`,
            point,
            suffix,
            estDate,
            subDate,
          };
        });

        // for our extra dates for the certain statuses, we push to our entries
        if (needsExtraEstDate) {
          const resubmissionSuffix = String.fromCharCode(
            65 + (point.resubmission_counter || 0),
          ).toLowerCase();
          const fallbackDate =
            point.progress_point === 6
              ? evaluator_checkout_date
              : validator_checkout_date;

          entries.push({
            key: `${point.product.product_id}-${point.progress_point}${resubmissionSuffix}-${point.prog_id}`,
            point,
            suffix: `${resubmissionSuffix}`,
            estDate: fallbackDate,
            subDate: undefined,
          } as any);
        }

        return entries;
      })
      .flat();
  }, [progress_points, evaluator_checkout_date, validator_checkout_date]);

  if (isError) {
    // WARN: Custom styling from pre standardization!
    // TODO: Standardize when you come across this!
    return (
      <Typography
        variant="caption"
        sx={{ color: "text.disabled", fontStyle: "italic", p: 2 }}
      >
        Error loading progress points
      </Typography>
    );
  }

  if (isLoading || !progress_points) {
    return <CircularProgress size={20} />;
  }

  if (preparedProgressPointRows.length === 0) {
    // WARN: Custom styling from pre standardization!
    // TODO: Standardize when you come across this!
    return (
      <Typography
        variant="caption"
        sx={{ color: "text.disabled", fontStyle: "italic", p: 2 }}
      >
        Not Started
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
      <Table size="small" sx={{ minWidth: 200 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 40, padding: "4px 8px" }}>
              Pt.
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: 80, padding: "4px 8px" }}>
              Estimated
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: 80, padding: "4px 8px" }}>
              Actual
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {preparedProgressPointRows.map((rowData) => (
            <ProgressPointRow key={rowData.key} rowData={rowData} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProgressPointsCell;
